import { logError } from '@ardoq/logging';
import { ArdoqId, IMPLICIT_REFTYPE_ID } from '@ardoq/api-types';
import { getModelIdFromReference } from './getModelIdFromReference';
import type {
  AggregatedReferenceType,
  EnhancedScopeData,
  GlobalReferenceTypeId,
} from '@ardoq/data-model';
import { getCurrentLocale, localeCompare } from '@ardoq/locale';
import { toIdSet } from '@ardoq/core';

export const hasReferenceType = (
  referenceTypeId: number,
  workspaceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  const modelId = enhancedScopeData.workspacesById[workspaceId]?.componentModel;
  return Boolean(
    enhancedScopeData.typesByModelId[modelId]?.referenceTypesById?.[
      referenceTypeId
    ]
  );
};

export const getReferenceTypesByFieldId = (
  fieldId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const field = enhancedScopeData.fieldsById[fieldId];
  return (
    enhancedScopeData.typesByModelId[field.model]?.referenceTypesById ?? {}
  );
};

export const getReferenceTypeIdAndParentIdByReferenceId = (
  id: string,
  enhancedScopeData: Pick<
    EnhancedScopeData,
    'referencesById' | 'modelsById' | 'typesByModelId' | 'workspacesById'
  >
) => {
  const referenceTypeAndModel = getReferenceTypeAndModelByReferenceId(
    id,
    enhancedScopeData
  );

  return referenceTypeAndModel
    ? {
        entityId: referenceTypeAndModel.referenceType.id,
        parentEntityId: referenceTypeAndModel.model._id,
      }
    : null;
};

/**
 * @returns the reference type (if found) regardless of whether it is a parent-child relationship or not
 */
export const getReferenceTypeByReferenceId = (
  id: string,
  enhancedScopeData: Pick<
    EnhancedScopeData,
    'referencesById' | 'modelsById' | 'typesByModelId' | 'workspacesById'
  >
) => {
  const referenceTypeAndModel = getReferenceTypeAndModelByReferenceId(
    id,
    enhancedScopeData
  );
  return referenceTypeAndModel?.referenceType ?? null;
};

/**
 * We differentiate between references that build the contextual hierarchy of a node (parent-child
 * relationships, i.e. "virtual" references), and references that build a network of nodes (are
 * "lateral" relationships between nodes). This function returns the reference type of the latter.
 *
 * @returns null if reference is a parent-child relationship, the reference type otherwise
 */
export const getLateralReferenceTypeByReferenceId = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  const reference = scopeData.referencesById[referenceId];
  // A parent-child reference is not a lateral reference. It is either present
  // and flagged `virtual`, or absent entirely (e.g. partitioned out of a
  // traversal's scope data and carried separately on the loadedGraph). Both
  // mean "no lateral reference type" — treat them the same and don't route the
  // missing case through getReferenceTypeByReferenceId, which logs an error.
  if (!reference || reference.virtual) return null;
  return getReferenceTypeByReferenceId(referenceId, scopeData);
};

export const getReferenceTypeId = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  try {
    const referenceType = getLateralReferenceTypeByReferenceId(
      scopeData,
      referenceId
    );

    return referenceType ? referenceType.id : null;
  } catch (e) {
    return null;
  }
};

export const getReferenceTypeNameFilter =
  (enhancedScopeData: EnhancedScopeData, typeName: string) =>
  (referenceId: ArdoqId) =>
    getReferenceTypeByReferenceId(referenceId, enhancedScopeData)?.name ===
    typeName;

const getReferenceTypeAndModelByReferenceId = (
  id: string,
  enhancedScopeData: Pick<
    EnhancedScopeData,
    'referencesById' | 'modelsById' | 'typesByModelId' | 'workspacesById'
  >
) => {
  const reference = enhancedScopeData.referencesById[id];
  if (!reference) {
    logError(Error('missing reference in enhancedScopeData.referencesById'));
    return null;
  }
  const modelId = getModelIdFromReference(reference, enhancedScopeData);
  if (!modelId) {
    logError(Error('missing model in enhancedScopeData.modelsById'));
    return null;
  }
  const referenceType =
    enhancedScopeData.typesByModelId[modelId]?.referenceTypesById[
      reference.type
    ];
  if (!referenceType) {
    logError(
      Error(
        'missing reference type in enhancedScopeData.typesByModelId[<ModelId>].referenceTypesById[<ReferenceTypeId>]'
      )
    );
    return null;
  }
  return { referenceType, model: enhancedScopeData.modelsById[modelId] };
};

export const getEditableReferenceTypes = (
  modelId: string,
  enhancedScopeData: EnhancedScopeData
) =>
  Object.values(
    enhancedScopeData.typesByModelId[modelId]?.referenceTypesById ?? {}
  ).filter(referenceType => referenceType.id !== IMPLICIT_REFTYPE_ID);

export const getGlobalReferenceType = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  const referenceType = getLateralReferenceTypeByReferenceId(
    scopeData,
    referenceId
  );
  const reference = scopeData.referencesById[referenceId];
  if (!referenceType || !reference) return null;
  return {
    id: `${reference.rootWorkspace}-${referenceType.id}`,
    name: referenceType.name,
  };
};

type GetReferenceTypes = (scopeData: EnhancedScopeData) => {
  traversedOutgoingReferenceTypes: AggregatedReferenceType[];
  traversedIncomingReferenceTypes: AggregatedReferenceType[];
};

export const getReferenceTypesInScope: GetReferenceTypes = scopeData => {
  const referenceIds = toIdSet(scopeData.references);
  const traversedOutgoingReferenceTypes = mapToDictionaryList(
    scopeData,
    referenceIds
  );
  const traversedIncomingReferenceTypes = mapToDictionaryList(
    scopeData,
    referenceIds
  );
  return { traversedOutgoingReferenceTypes, traversedIncomingReferenceTypes };
};

const mapToDictionaryList = (
  scopeData: EnhancedScopeData,
  set: Set<ArdoqId>
): AggregatedReferenceType[] => {
  const locale = getCurrentLocale();

  return Array.from(
    Array.from(set)
      .reduce((map, referenceId: ArdoqId) => {
        const refType = getGlobalReferenceType(scopeData, referenceId);
        if (refType) {
          if (!map.has(refType.name)) {
            map.set(refType.name, new Set<GlobalReferenceTypeId>());
          }
          map.get(refType.name)!.add(refType.id);
        }
        return map;
      }, new Map<string, Set<GlobalReferenceTypeId>>())
      .entries()
  )
    .map(([name, typeIds]) => ({ name, typeIds: Array.from(typeIds) }))
    .sort((a, b) => localeCompare(a.name, b.name, locale));
};
